import { useEffect, useState } from "react";
import { Shell, type AppRoute } from "./components/Shell";
import { WeSellProvider, useWeSell } from "./state/WeSellProvider";
import { useSession } from "./state/useSession";
import { Dashboard } from "./views/Dashboard";
import { Products } from "./views/Products";
import { Content } from "./views/Content";
import { Pauta } from "./views/Pauta";
import { Links } from "./views/Links";
import { Sales } from "./views/Sales";
import { Connections } from "./views/Connections";
import { StorefrontSettings } from "./views/StorefrontSettings";
import { PublicStorefront } from "./views/PublicStorefront";
import { Login, PasswordRecovery } from "./views/Login";
import { Platforms } from "./views/Platforms";

const validRoutes = new Set<AppRoute>([
  "dashboard",
  "produtos",
  "pauta",
  "conteudos",
  "links",
  "vendas",
  "plataformas",
  "conexoes",
  "vitrine",
]);

function routeFromHash(): AppRoute {
  const value = window.location.hash.replace("#/", "") as AppRoute;
  return validRoutes.has(value) ? value : "dashboard";
}

function PrivateApp({
  isDemo,
  onSignOut,
}: {
  isDemo: boolean;
  onSignOut: () => void;
}) {
  const [route, setRoute] = useState(routeFromHash);
  const { data, loading, saving, error, restoreDemo } = useWeSell();
  useEffect(() => {
    const change = () => setRoute(routeFromHash());
    window.addEventListener("hashchange", change);
    return () => window.removeEventListener("hashchange", change);
  }, []);
  const navigate = (next: AppRoute) => {
    window.location.hash = `/${next}`;
    setRoute(next);
  };
  if (loading)
    return (
      <main className="public-loading">
        <span className="spinner" />
        <span>Organizando seu WeSell…</span>
      </main>
    );
  return (
    <Shell
      route={route}
      navigate={navigate}
      isDemo={isDemo}
      saving={saving}
      onReset={restoreDemo}
      onSignOut={onSignOut}
    >
      {error ? (
        <div className="global-error" role="alert">
          {error}
        </div>
      ) : null}
      {!data ? (
        <p>Não foi possível carregar os dados.</p>
      ) : route === "dashboard" ? (
        <Dashboard navigate={(value) => navigate(value as AppRoute)} />
      ) : route === "produtos" ? (
        <Products />
      ) : route === "pauta" ? (
        <Pauta />
      ) : route === "conteudos" ? (
        <Content />
      ) : route === "links" ? (
        <Links />
      ) : route === "vendas" ? (
        <Sales />
      ) : route === "plataformas" ? (
        <Platforms />
      ) : route === "conexoes" ? (
        <Connections />
      ) : (
        <StorefrontSettings />
      )}
    </Shell>
  );
}

// A vitrine abre em dois formatos de endereço: o curto, que é o que vai no
// domínio próprio (lalasdiaries.com/achadinhos), e o antigo com /v/ na frente,
// mantido para não quebrar link já publicado. O painel privado vive na raiz e
// navega por hash, então nunca disputa caminho com a vitrine.
function publicSlugFromPath(): string | null {
  const match = window.location.pathname.match(
    /^\/(?:v\/)?([a-z0-9][a-z0-9-]{1,48})\/?$/i,
  );
  return match ? match[1] : null;
}

export default function App() {
  const publicSlug = publicSlugFromPath();
  const { state, signOut, isDemo } = useSession();
  if (publicSlug) return <PublicStorefront slug={publicSlug} />;
  if (state === "loading")
    return (
      <main className="public-loading">
        <span className="spinner" />
        <span>Verificando acesso…</span>
      </main>
    );
  if (state === "anonymous") return <Login />;
  if (state === "recovery") return <PasswordRecovery />;
  return (
    <WeSellProvider isDemo={isDemo}>
      <PrivateApp
        isDemo={isDemo}
        onSignOut={() => {
          if (isDemo) window.location.href = "/";
          else void signOut();
        }}
      />
    </WeSellProvider>
  );
}
